import { useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { ChallengeSessionId } from 'src/models/Challenge';
import { Session } from 'src/components';
import { ChallengesService } from 'src/services';
import {
  PlaylistActionsMenu,
  canAddToPlaylist,
  resolvePlaylistContentType,
} from 'src/features/AddToPlaylist';
import { useChallengeSession } from './hooks';

const invalidateLikesQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  void queryClient.invalidateQueries(['shortActivityList', 'Ulubione']);
  void queryClient.invalidateQueries(['likes']);
};

const ChallengeSession: React.FC<{
  id: ChallengeSessionId;
  isCompleted: boolean;
  onToggleCompleted: () => void;
}> = ({
  id,
  isCompleted,
  onToggleCompleted,
}) => {
  const queryClient = useQueryClient();
  const { data: session } = useChallengeSession(id);

  const playlistContentType = useMemo(
    () => (session ? resolvePlaylistContentType(session) : 'session'),
    [session],
  );


  if (!session) return null;

  return (
    <Session
      contentType={session.contentType}
      title={session.title}
      underline={session.underline}
      difficulty={session.difficulty}
      primaryDescription={session.primaryDescription}
      secondaryDescription={session.secondaryDescription}
      coach={session.coach}
      style={session.style as any}
      type={session.type as any}
      tag={session.tag as any}
      equipment={session.equipment as any}
      video={session.video}
      horizontalThumbnail={session.horizontalThumbnail}
      attachments={session.extras}
      isCompleted={isCompleted}
      toggleCompleted={onToggleCompleted}
      duration={session.video?.duration}
      isLiked={session.isLiked}
      toggleLike={(liked) =>
        ChallengesService.toggleChallengeSessionLiked(session.id, liked).then(
          () => invalidateLikesQueries(queryClient),
        )
      }
      playlistActions={
        canAddToPlaylist(session) ? (
          <PlaylistActionsMenu
            contentId={session.id}
            contentType={playlistContentType}
            variant="detail"
          />
        ) : undefined
      }
      entityId={id}
      entityType="challenge-session"
    />
  );
};

export default ChallengeSession;
